import {safelyParsePage} from "@rw/string_utils";
import {Dispatch} from "redux";

import {IStore} from "../../../app/reducers/hybrid_reducer";
import {IRouteState} from "../../../app/routes/data_fetcher/create_app_path_data_fetcher";
import {IServices} from "../../../app/services/IServices";
import {getVoivodeshipLocativeName, Voivodeship, voivodeshipNames} from "../../../app/types/locationTypes";
import {carsCommonUrl} from "../../../app/utils/read_environment_variables";
import {CarMakeTypes} from "../../../cars_data/cars_data";
import {updateMetaData} from "../../../meta_data/actions/update_meta_data";
import {getOfferListUrl} from "../url_utils/get_offer_list_url";
import {parseOfferListPath} from "../url_utils/parse_offer_list_path";
import {capitalizeFirstLetter} from "../utils/capitalize_first_letter";
import {formatMakeDisplayName} from "../utils/format_make_display_name";
import {formatModelDisplayName} from "../utils/format_model_display_name";
import {
    buildCarListCanonical,
    buildCarListDocumentTitle,
    buildCarListRobots,
    buildLocationMetaDescription,
    LISTING_SITE_NAME
} from "../utils/list_location_copy";
import {ILocation} from "./fetch_location_by_slug_at_route";

interface IProps {
    offersCount: number;
    currentLocationName: ILocation | null;
    /** Polish city label from get-pl-location when available. */
    cityDisplayName?: string;
    offerType: string;
    page: number;
    type?: CarMakeTypes;
    model?: string;
    tag?: string;
    location?: string;
    subLocation?: string;
}

/** Nominative place for titles without "w" (Częstochowa, Śląskie). */
const formatPlaceName = (props: IProps): string => {
    const {cityDisplayName, location, subLocation} = props;
    if (subLocation) {
        return cityDisplayName || capitalizeFirstLetter(subLocation);
    }
    if (location) {
        const slug = location.toLowerCase() as Voivodeship;
        return voivodeshipNames[slug] || capitalizeFirstLetter(location);
    }
    return "";
};

const makeModelLabel = (make: string, model?: string): string => {
    const modelLabel = formatModelDisplayName(model);
    if (make && modelLabel) {
        return `${make} ${modelLabel}`;
    }
    return make || modelLabel;
};

const listingCopyInput = (props: IProps) => {
    const make = formatMakeDisplayName(props.type);
    return {
        makeModel: makeModelLabel(make, props.model) || undefined,
        place: formatPlaceName(props) || undefined,
        count: props.offersCount,
        page: props.page
    };
};

export const getCarListTitle = (props: IProps) => buildCarListDocumentTitle(listingCopyInput(props));

const placePhrase = (props: IProps): string => {
    const place = formatPlaceName(props);
    if (props.subLocation && place) {
        return `miejscowości ${place}`;
    }
    if (props.location && place) {
        const locative = getVoivodeshipLocativeName(props.location.toLowerCase());
        return `województwie ${locative || place}`;
    }
    return "";
};

export const getCarListDescription = (props: IProps) => {
    const {makeModel} = listingCopyInput(props);
    return buildLocationMetaDescription({
        count: props.offersCount,
        placePhrase: placePhrase(props),
        makeModel,
        page: props.page
    });
};

export const updateCarListMetaData =
    (
        _1: Partial<IServices>,
        route: IRouteState<{
            offerType: string;
            type?: CarMakeTypes;
            tag?: string;
            location?: string;
            subLocation?: string;
            subSubLocation?: string;
        }>
    ) =>
    (dispatch: Dispatch, getState: () => IStore) => {
        const state = getState();
        const page = safelyParsePage(route.query.page);
        const currentLocationName = state.carList.location.location;
        const parsed = parseOfferListPath(route.params);
        const offersCount = state.carList.cars.totalItems || 0;
        const cityDisplayName = state.carList.locationPLName?.cityName || undefined;

        const metaProps: IProps = {
            offersCount,
            currentLocationName,
            cityDisplayName,
            offerType: route.params.offerType,
            page,
            type: (parsed.type || route.params.type) as CarMakeTypes | undefined,
            model: parsed.model,
            tag: route.params.tag,
            location: parsed.location,
            subLocation: parsed.subLocation
        };

        const title = getCarListTitle(metaProps);
        const description = getCarListDescription(metaProps);
        const hideSuffix = title.includes(`| ${LISTING_SITE_NAME}`);

        const listPath = getOfferListUrl({
            offerType: route.params.offerType as any,
            type: parsed.type || route.params.type,
            model: parsed.model,
            location: parsed.location,
            subLocation: parsed.subLocation,
            tag: route.params.tag as any
        });
        const canonical = buildCarListCanonical(carsCommonUrl, listPath, page);
        const robots = buildCarListRobots(offersCount, page);

        dispatch(
            updateMetaData(title, description, route, {
                canonical,
                robots,
                hideSuffix
            })
        );
        return Promise.resolve(true);
    };
